import { COMPONENTS } from '@theme/registry';

/**
 * Loads component modules based on what is actually in the DOM.
 *
 * The theme's loading strategy in one file (see
 * `docs/component-architecture.md`, «Cómo se carga»). Sections and blocks do not emit
 * `<script>` tags for their components; they just render the custom element and
 * this decides what to fetch.
 *
 * Why a runtime scan rather than gating in Liquid by template: the real question
 * is never "which template is this?" but "is there a `product-card` here, now or
 * in a moment?". `blocks/product-card.liquid` accepts `product-quick-add` as a
 * child and `products-carousel` is only barred from the header and footer, so a
 * merchant can put a quick-add carousel on any page from the editor — and the
 * PDP carousels and the cart drawer's empty state inject cards over AJAX, long
 * after Liquid has had its say. Any template gate is wrong by construction.
 *
 * This does not add machinery, it replaces machinery: one MutationObserver here
 * instead of the five that were scattered through `product-media.js`,
 * `quick-add.js` and friends, plus the two globals (`window.initPdpCarousels`,
 * `window.TaoCollectionPagination`) whose only job was re-wiring AJAX markup by
 * hand. Both are gone.
 *
 * Each module is fetched at most once. The modules themselves call
 * `customElements.define`, and the browser upgrades every matching element
 * already in the document — including ones that arrive later, and including
 * `cloneNode` copies, which is what fixes the frozen countdown in a looping hero
 * slide.
 */

/** Tags whose module has not been imported yet. */
const pending = new Set(COMPONENTS.keys());

/**
 * `pending` joined into one selector, so a scan costs one `matches` plus one
 * `querySelector` instead of one of each per registered tag. Recomputed
 * whenever `pending` shrinks; never read while `pending` is empty, because an
 * empty selector string throws.
 */
let selector = [...pending].join(',');

/** @param {string} tag */
function load(tag) {
  const loader = COMPONENTS.get(tag);
  if (!loader || !pending.delete(tag)) return;

  selector = [...pending].join(',');

  loader().catch((error) => {
    console.error(`[bootstrap] failed to load <${tag}>`, error);
  });
}

/**
 * Imports the module for every registered tag present in `root` or below it.
 *
 * @param {Element | Document} root
 */
function scan(root) {
  if (!pending.size) return;

  const isElement = root instanceof Element;

  // One cheap question first: does this subtree contain anything at all we still
  // care about? Almost always no — this runs for every node the page adds.
  if (!(isElement && root.matches(selector)) && !root.querySelector(selector)) return;

  for (const tag of [...pending]) {
    if ((isElement && root.matches(tag)) || root.querySelector(tag)) load(tag);
  }
}

const observer = new MutationObserver((records) => {
  for (const record of records) {
    for (const node of record.addedNodes) {
      if (node.nodeType !== Node.ELEMENT_NODE) continue;
      scan(/** @type {Element} */ (node));
    }
  }

  // Nothing left to look for. Stop watching the document.
  if (!pending.size) observer.disconnect();
});

// Modules are deferred, so the document is fully parsed by the time this runs.
scan(document);

if (pending.size) {
  observer.observe(document.documentElement, { childList: true, subtree: true });
}
